import { cn } from "@/shared/lib"

export interface GiftThumbnailItem {
  /** Stable id for React keys (betId works fine). */
  id: string
  smallImageUrl: string | null
}

/**
 * `row`    — bet row under the wheel: mini overlapping strip on `bg-white/10`.
 * `bubble` — activity-feed bubble above the wheel: single gift = one large
 *            thumbnail with no outer pill; multiple = mini strip on
 *            `bg-secondary-main`.
 *
 * Both variants collapse 1..3 → all visible, 4+ → 2 thumbs + "+{N-2}".
 */
export type GiftBetThumbnailsTone = "row" | "bubble"

interface GiftBetThumbnailsProps {
  gifts: GiftThumbnailItem[]
  tone?: GiftBetThumbnailsTone
}

const MAX_VISIBLE_MINI_THUMBS = 3

export function GiftBetThumbnails({
  gifts,
  tone = "row",
}: GiftBetThumbnailsProps) {
  if (gifts.length === 0) return null

  if (tone === "bubble" && gifts.length === 1) {
    return <LargeGiftThumb gift={gifts[0]} />
  }

  const total = gifts.length
  const showOverflow = total > MAX_VISIBLE_MINI_THUMBS
  const visibleCount = showOverflow ? 2 : total
  const visible = gifts.slice(0, visibleCount)
  const remaining = total - visibleCount

  const stripBg = tone === "bubble" ? "bg-secondary-main" : "bg-white/10"

  return (
    <div className={cn("flex h-[25px] items-center gap-1 rounded-md px-1", stripBg)}>
      <div className="flex items-center">
        {visible.map((g, idx) => (
          <MiniGiftThumb key={g.id} gift={g} isFirst={idx === 0} />
        ))}
      </div>
      {showOverflow && (
        <span className="text-[10px] font-semibold leading-3 tracking-[-0.5px] text-white">
          +{remaining}
        </span>
      )}
    </div>
  )
}

function LargeGiftThumb({ gift }: { gift: GiftThumbnailItem }) {
  return (
    <div
      className={cn(
        "h-[25px] w-[25px] shrink-0 rounded-md bg-cover bg-center",
        !gift.smallImageUrl && "bg-secondary-main"
      )}
      style={
        gift.smallImageUrl
          ? { backgroundImage: `url(${gift.smallImageUrl})` }
          : undefined
      }
      aria-hidden
    />
  )
}

function MiniGiftThumb({
  gift,
  isFirst,
}: {
  gift: GiftThumbnailItem
  isFirst: boolean
}) {
  return (
    <div
      className={cn(
        "h-4 w-4 shrink-0 rounded-[5px] bg-cover bg-center ring-1 ring-secondary-main",
        !gift.smallImageUrl && "bg-white/20",
        !isFirst && "-ml-2"
      )}
      style={
        gift.smallImageUrl
          ? { backgroundImage: `url(${gift.smallImageUrl})` }
          : undefined
      }
    />
  )
}
